import React, { useState, useMemo } from 'react'
import lodash from 'lodash'
import PlafondInput from './PlafondInput'
import PlafondWarning from './PlafondWarning'
import { PROFILS, PROFIL_KEYS } from '../constants'
import { PlafondRemise, LibelleRemise } from '../../../types/PlafondRemise'
import { Societe } from "../../../types/Societe"

interface LibelleRemiseCollapseProps {
    libelle: LibelleRemise
    plafondsRemise: PlafondRemise[]
    societes: Societe[]
    onPlafondChange: (IDSociete: number | null, IDProfil: number, value: string | number | null, IDLibelleRemise: number | null) => void
    defaultOpen?: boolean
}

const LibelleRemiseCollapse: React.FC<LibelleRemiseCollapseProps> = ({
    libelle,
    plafondsRemise,
    societes,
    onPlafondChange,
    defaultOpen = false
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(defaultOpen)

    const { plafondsRemiseParSociete, plafondsRemiseGroupe } = useMemo(() => ({
        plafondsRemiseParSociete: lodash.groupBy(
            plafondsRemise.filter(p => p.IDLibelleRemise === libelle.IDLibelleRemise),
            p => p.IDPlaque
        ),
        plafondsRemiseGroupe: lodash.keyBy(
            plafondsRemise.filter(p => p.IDPlaque == null && p.IDLibelleRemise === libelle.IDLibelleRemise),
            "IDDRProfileEsellerLead"
        )
    }), [plafondsRemise, libelle.IDLibelleRemise])

    const isVendeurSuperieurCdv = (vendeurPlafond: string | number | null | undefined, cdvPlafond: string | number | null | undefined): boolean => {
        return Number(vendeurPlafond) > Number(cdvPlafond)
    }

    const handlePlafondChange = (IDSociete: number | null, IDProfil: number, value: string | number | null) => {
        onPlafondChange(IDSociete, IDProfil, value, libelle.IDLibelleRemise)
    }

    return (
        <div className="card mb-2">
            <div
                className="card-header d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>{libelle.Libelle}</span>
                <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'}`} />
            </div>

            {isOpen && (
                <div className="card-body p-3">
                    <table className="table table-striped table-sm w-auto mb-0">
                        <tbody>
                            {/* En-tête Groupe */}
                            <tr>
                                <th scope="col">
                                    Groupe<br />
                                    <small className="text-muted">
                                        Si aucun plafond n'est défini, alors le montant des remises est libre
                                    </small>
                                </th>
                                <th scope="col" className="align-bottom">Vendeurs</th>
                                <th scope="col" className="align-bottom">Chefs de ventes</th>
                                <th scope="col" className="d-none"></th>
                            </tr>

                            {/* Ligne plafond global groupe */}
                            <tr>
                                <td>Plafond de remise global au groupe pour les sociétés non configurées</td>
                                {PROFIL_KEYS.map(profilKey => {
                                    const profilId = PROFILS[profilKey]
                                    return (
                                        <td key={profilKey}>
                                            <PlafondInput
                                                value={plafondsRemiseGroupe[profilId]?.Plafond}
                                                onChange={(value) => handlePlafondChange(null, profilId, value)}
                                            />
                                        </td>
                                    )
                                })}
                                <td className="border-0 bg-transparent">
                                    <PlafondWarning
                                        show={isVendeurSuperieurCdv(
                                            plafondsRemiseGroupe[PROFILS.VEND]?.Plafond,
                                            plafondsRemiseGroupe[PROFILS.CDV]?.Plafond
                                        )}
                                    />
                                </td>
                            </tr>

                            {/* En-tête Sociétés */}
                            <tr>
                                <th scope="col">
                                    Sociétés <br />
                                    <small className="text-muted">
                                        Si aucun plafond n'est défini, alors le plafond du groupe s'applique
                                    </small>
                                </th>
                                <th scope="col" className="align-bottom">Vendeurs</th>
                                <th scope="col" className="align-bottom">Chefs de ventes</th>
                            </tr>

                            {/* Lignes par société */}
                            {societes.map((societe) => {
                                const plafondsParProfil = lodash.keyBy(
                                    plafondsRemiseParSociete[societe.IDPlaque],
                                    "IDDRProfileEsellerLead"
                                )
                                const atLeastOnePlafondIsDefined = !!plafondsRemiseParSociete[societe.IDPlaque]?.length

                                const vendeurPlafond = (plafondsParProfil[PROFILS.VEND] ?? plafondsRemiseGroupe[PROFILS.VEND])?.Plafond
                                const cdvPlafond = (plafondsParProfil[PROFILS.CDV] ?? plafondsRemiseGroupe[PROFILS.CDV])?.Plafond

                                return (
                                    <tr key={societe.IDPlaque}>
                                        <td>{societe.Libelle}</td>
                                        {PROFIL_KEYS.map(profilKey => {
                                            const profilId = PROFILS[profilKey]
                                            return (
                                                <td key={profilKey}>
                                                    <PlafondInput
                                                        value={plafondsParProfil[profilId]?.Plafond}
                                                        onChange={(value) => handlePlafondChange(societe.IDPlaque, profilId, value)}
                                                    />
                                                </td>
                                            )
                                        })}
                                        <td className="border-0 bg-white">
                                            <PlafondWarning
                                                show={atLeastOnePlafondIsDefined && isVendeurSuperieurCdv(vendeurPlafond, cdvPlafond)}
                                            />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

export default LibelleRemiseCollapse
